import type { TicketRedemptionCodeFormDisplayText } from '@vista-digital/occ-ui'

// eslint-disable-next-line @typescript-eslint/naming-convention -- global consts
export const MAPFRE_TOKEN_STORAGE_KEY = 'mapfre-token'

export interface MapfreDetails {
    // Error message only included when non 200 status returned. If error is included it will be the only property.
    error?: string

    // If card number is used in a transaction, this unique id should be stored against the orders postcode field.
    uniqueId: string

    // To be redeem as a voucher against the La Vanguardia voucher type.
    cardNumber: string

    // Expiry time as unix seconds.
    timestamp: string

    // Timestamp converted to a proper date by the backend.
    expireAt: Date
}

export class MapfreRedemptionErrorsPropsApi implements TicketRedemptionCodeFormDisplayText {
    /**
     * The error message displayed when the voucher barcode or PIN was incorrect and failed to be added.
     */
    errorVoucherCredentialsInvalidMessage: string = 'The voucher number or PIN you entered was incorrect'

    /**
     * The error messaged displayed when the user attempts to add a voucher that is already in their order.
     */
    errorVoucherAlreadyAddedMessage: string = 'The voucher you entered has already been added to your order'

    /**
     * The error message displayed when the third-party member card was declined.
     */
    errorThirdPartyMemberCardDeclinedMessage: string = 'The card you entered could not be added'

    /**
     * The error message displayed when the user attempts to add a third-party member card that is already in their order.
     */
    errorThirdPartyMemberCardAlreadyAddedMessage: string = 'The card you entered has already been added to your order'

    /**
     * The error message displayed when the user attempts to add a third-party member card that does not have a valid ticket for a showtime.
     */
    errorNoValidTicketsAvailableForThirdPartyMemberCardMessage: string =
        'The card you entered cannot be used for this showtime'

    /**
     * The error message displayed when an unknown error occurred while trying to add their voucher or card.
     */
    errorFailedToAddMessage: string = 'Your card or voucher could not be added'

    /**
     * The error message to be displayed when voucher is unavailable for the distributor.
     */
    errorTicketVoucherInvalidForFilmDistributorMessage: string =
        'This voucher is not available for films from this distributor.'

    /**
     * The error message to be displayed when the voucher is unavailable on public holidays.
     */
    errorTicketVoucherInvalidForPublicHolidayMessage: string = 'This voucher is not available on public holidays.'

    /**
     * The error message to be displayed when voucher is unavailable for the film.
     */
    errorTicketVoucherInvalidForFilmMessage: string = 'This voucher is not available for this film.'

    /**
     * The error message to be displayed when voucher is unavailable for the film from a particular date.
     */
    errorTicketVoucherInvalidForFilmFromDateMessage: string =
        'This voucher is not available for this film from ${startDate|d|DateShort}.'

    /**
     * The error message to be displayed when voucher is unavailable for the film until a particular date.
     */
    errorTicketVoucherInvalidForFilmUntilDateMessage: string =
        'This voucher is not available for this film until ${endDate|d|DateShort}.'

    /**
     * The error message to be displayed when voucher is unavailable for the film in a particular time period.
     */
    errorTicketVoucherInvalidForFilmBetweenDatesMessage: string =
        'This voucher is not available for this film from ${startDate|d|DateShort} until ${endDate|d|DateShort}.'

    /**
     * The error message shown when voucher is unavailable for this film on a particular day.
     */
    errorTicketVoucherInvalidForFilmOnDateMessage: string =
        'This voucher is not available for this film on ${endDate|d|DateShort}.'

    /**
     * The error message displayed when the voucher has been invalidated in VGC and cannot be used.
     */
    errorVoucherInvalidatedMessage: string = 'This voucher cannot be used'

    /**
     * The error message displayed when the voucher has no redemptions left.
     */
    errorNoVoucherRedemptionsLeftMessage: string = 'This voucher has no redemptions left'

    /**
     * The error message displayed when the voucher has expired.
     */
    errorVoucherExpiredMessage: string = 'This voucher has expired'

    /**
     * The error message displayed when the voucher type is incompatible with the order.
     */
    errorVoucherInvalidForOrderMessage: string = 'This voucher is not valid for this order'
    errorFailedToLoadThirdPartyMemberSchemesMessage: string = 'Failed to load third party member schemes'
    voucherCodeTypeLabel: string = 'Voucher'
}
